import { API_CONFIG } from '@/config/api';
import { getEtag, setEtag } from '@/lib/etagStore';
import { useApiErrorStore } from '@/stores/apiErrorStore';
import axios from 'axios';
import { generateHmacSignature } from './hmac';

if (!API_CONFIG.baseURL) {
  throw new Error('VITE_API_BASE_URL is not defined');
}

const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: 10000,
  headers: {
    Accept: 'application/json',
  },
});

/* ================= REQUEST ================= */
api.interceptors.request.use((config) => {
  const timestamp = Math.floor(Date.now() / 1000);
  const method = (config.method || 'GET').toUpperCase();

  const url = new URL(config.url, API_CONFIG.baseURL);
  const path = url.pathname.replace(/^\/+/, '');

  const signature = generateHmacSignature(timestamp, method, path, API_CONFIG.hmacSecret);

  config.headers = {
    ...config.headers,
    'X-API-KEY': API_CONFIG.apiKey,
    'X-TIMESTAMP': timestamp.toString(),
    'X-SIGNATURE': signature,
  };

  // 🏦 ETag → kirim If-None-Match (GET saja)
  if (method === 'GET') {
    const etag = getEtag(path);
    if (etag) {
      config.headers['If-None-Match'] = etag;
    }
  }

  return config;
});

/* ================= RESPONSE ================= */
api.interceptors.response.use(
  (res) => {
    // 🏦 Simpan ETag dari backend
    const etag = res.headers['etag'];

    if (etag) {
      const url = new URL(res.config.url, API_CONFIG.baseURL);
      const path = url.pathname.replace(/^\/+/, '');
      setEtag(path, etag);
    }

    return res;
  },
  (error) => {
    const status = error?.response?.status;

    // ✅ 304 = data masih valid → JANGAN error
    if (status === 304) {
      return Promise.resolve({
        status: 304,
        data: null,
        config: error.config,
      });
    }

    // ⛔ Rate limit (punyamu tetap dipakai)
    if (status === 429) {
      const store = useApiErrorStore.getState();

      if (store.isRateLimited()) {
        return Promise.reject(error);
      }

      const headers = error.response.headers;
      const retryAfterHeader = Number(headers['retry-after']);
      const resetHeader = Number(headers['x-ratelimit-reset']);

      let retryAfterSec;

      if (!Number.isNaN(retryAfterHeader)) {
        retryAfterSec = retryAfterHeader;
      } else if (!Number.isNaN(resetHeader)) {
        retryAfterSec = Math.max(0, Math.ceil((resetHeader * 1000 - Date.now()) / 1000));
      } else {
        retryAfterSec = 30;
      }

      const endTime = Date.now() + retryAfterSec * 1000;

      sessionStorage.setItem('rateLimitEndTime', endTime.toString());

      store.setRateLimit({
        message: `Terlalu banyak permintaan. Silakan tunggu ${retryAfterSec} detik.`,
        retryAfter: retryAfterSec,
        endTime,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
